import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAtom } from 'jotai'
import { atomWithGlobalStore } from '@highlight/ipc/jotai'
import { useReminderActions } from '../../hooks/use-reminders'
import type { ReminderItem } from '../../hooks/use-reminders'
import { useRemindersData } from '../../reminders-context'
import type { TimeframeRow } from '@renderer/overlay/Sidepanel/utils/group-by-timeframe'
import {
  filterItems,
  normalizeFilter,
  orderItemsStably,
  toReminderRows,
  type ReminderFilterType,
} from './reminders-view-order'

/** Persisted so reopening the drawer lands on the tab the user last chose. */
const remindersFilterAtom = atomWithGlobalStore<ReminderFilterType>('general', 'sidepanel.reminders.filter', 'active')

export interface RemindersViewContext {
  initialTab?: ReminderFilterType
  focusReminderId?: string
}

export interface RemindersViewModel {
  filter: ReminderFilterType
  setFilter: (filter: ReminderFilterType) => void
  searchQuery: string
  setSearchQuery: (query: string) => void
  /** The list as the virtualizer consumes it — group labels and items already interleaved. */
  rows: Array<TimeframeRow<ReminderItem>>
  counts: Record<ReminderFilterType, number>
  addItem: ReturnType<typeof useReminderActions>['addItem']
  focusReminderId: string | undefined
  /** Increments when the user jots a reminder so the list can scroll it into view. */
  createdTick: number
}

export function useRemindersViewModel(context: RemindersViewContext | undefined): RemindersViewModel {
  const { items, lingeringResolvedIds } = useRemindersData()
  const { addItem } = useReminderActions()
  const [storedFilter, setFilter] = useAtom(remindersFilterAtom)
  // A filter persisted before Open folded into Active, or under Pinned's old
  // name, has to land on a tab that still exists.
  const filter = normalizeFilter(storedFilter)
  const [searchQuery, setSearchQuery] = useState('')
  const [createdTick, setCreatedTick] = useState(0)
  const initialTab = context?.initialTab
  const focusReminderId = context?.focusReminderId

  // A toast/notification click carries its own tab, which wins over the remembered one —
  // on mount and again when the context updates in place on an already-open view. Clear
  // any stale search too, so the focused reminder isn't filtered out.
  useEffect(() => {
    if (initialTab) setFilter(initialTab)
    if (focusReminderId) setSearchQuery('')
  }, [initialTab, focusReminderId])

  // Jotting from the Pinned tab makes a pinned reminder — otherwise it'd land
  // somewhere the user can't see it from where they typed it.
  const handleAddItem: typeof addItem = useCallback(
    (title, type, scheduledAt, attachments, meta) => {
      addItem(title, type, scheduledAt, attachments, { ...meta, pinned: filter === 'pinned' })
      setCreatedTick((tick) => tick + 1)
    },
    [addItem, filter],
  )

  // Per-tab captured ordering. Seeded the first time a tab is viewed and kept stable
  // across live updates so the list doesn't re-prioritize while the user is browsing.
  const capturedOrderRef = useRef<Partial<Record<ReminderFilterType, Array<string>>>>({})

  const filteredItems = useMemo(() => {
    const matching = filterItems(items, filter, lingeringResolvedIds)
    const ordered = orderItemsStably(matching, capturedOrderRef.current[filter])
    // Lock the order in: seeds on first view, appends new arrivals on later updates.
    if (matching.length > 0) {
      capturedOrderRef.current[filter] = ordered.map((item) => item.id)
    }

    // Surface the reminder opened from a toast/notification at the top of the list.
    let result = ordered
    if (focusReminderId) {
      const focusIndex = result.findIndex((item) => item.id === focusReminderId)
      if (focusIndex > 0) {
        const focused = result[focusIndex]
        result = [focused, ...result.slice(0, focusIndex), ...result.slice(focusIndex + 1)]
      }
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      return result.filter((item) => item.title.toLowerCase().includes(query))
    }

    return result
  }, [filter, items, searchQuery, focusReminderId, lingeringResolvedIds])

  // Grouping is the tail of the same pipeline the filter drives, so it lives
  // here rather than in the view — which then needs to know nothing about what
  // any given filter means. A pinned reminder skips grouping outright: it sits
  // at the top, where any label above it would be the wrong one.
  const rows = useMemo<Array<TimeframeRow<ReminderItem>>>(
    () =>
      focusReminderId
        ? filteredItems.map((item, itemIndex) => ({ kind: 'item', item, itemIndex }))
        : toReminderRows(filteredItems, filter),
    [filteredItems, filter, focusReminderId],
  )

  // Counted the same way the rows are filtered, so a tab's count matches what
  // it draws while a completed reminder is still lingering in it.
  const counts = useMemo(
    () => ({
      all: filterItems(items, 'all', lingeringResolvedIds).length,
      active: filterItems(items, 'active', lingeringResolvedIds).length,
      pinned: filterItems(items, 'pinned', lingeringResolvedIds).length,
      resolved: filterItems(items, 'resolved', lingeringResolvedIds).length,
    }),
    [items, lingeringResolvedIds],
  )

  return {
    filter,
    setFilter,
    searchQuery,
    setSearchQuery,
    rows,
    counts,
    addItem: handleAddItem,
    focusReminderId,
    createdTick,
  }
}
